import { Component, DoCheck, ElementRef, EventEmitter, HostListener, inject, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { AsyncPipe, DecimalPipe, NgClass } from "@angular/common";
import { BetPlacementService } from "../../../../../../shared/src/lib/services/bet-placement.service";
import { MatIconModule } from "@angular/material/icon";
import { MatButton } from "@angular/material/button";
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { ApiService } from "../../../../../../shared/src";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { LiveUpdateService } from "../../../../../../shared/src/lib/services/live-update.service";
import { combineLatest, combineLatestWith, map, Observable, Subject } from "rxjs";
import { BpOverlayComponent } from "../../../components/bp-overlay/bp-overlay.component";
import { SnackbarService } from "../../../../../../shared/src/lib/services/snackbar.service";
import { CoreService } from "../../../../../../shared/src/lib/services/core.service";
import { validOdds } from "../../../../../../shared/src/lib/constants/valid-odds";
import { PopularEventsService } from "../../../../../../shared/src/lib/services/popular-events.service";
import { TranslateModule } from "@ngx-translate/core";
import { isNumeric, notNaNValidator, NumeralUtil, safeParseFloat } from "../../../../../../shared/src/lib/utils/common";
import { LoaderService } from "../../../../../../shared/src/lib/services/loader.service";
import { defaultStakes } from "../../../../assets/constants/default-stakes";
import { ExposureService } from "../../../../../../shared/src/lib/services/exposure.service";
import { Router } from "@angular/router";
import { PlacementOpsService } from "./placement-ops.service";
import { FeatureFlagDirective } from "../../../../../../shared/src/lib/directives/feature-flag.directive";
import { MarketProvider, PredictedBet } from "../../../../../../shared/src/lib/types/bet.type";
import { BetService } from "../../../../../../shared/src/lib/services/bet.service";
import { MarketOpsService } from "../../content/market-layout/market/market-ops.service";
import { DebuggingService } from "../../../components/debugging-modal/services/debugging.service";
import { BalanceService } from "../../../../../../shared/src/lib/services/balance.service";
import { ExposureDebuggingObject } from "../../../components/debugging-modal/types/debugging.type";
import { AhLadderPnlComponent } from "../../content/market-layout/market/asian-handicap-pnl/ah-ladder-pnl/ah-ladder-pnl.component";
import { takeUntil } from "rxjs/operators";
import { WhiteLabelConfigService } from "../../../../../../shared/src/lib/services/white-label-config.service";

@Component({
  selector: "app-placement-manager",
  imports: [
    MatIconModule,
    MatButton,
    NgClass,
    ReactiveFormsModule,
    MatSnackBarModule,
    MatProgressBarModule,
    BpOverlayComponent,
    DecimalPipe,
    TranslateModule,
    AsyncPipe,
    FeatureFlagDirective,
    AhLadderPnlComponent,
  ],
  templateUrl: "./placement-manager.component.html",
  styleUrl: "./placement-manager.component.scss",
})
export class PlacementManagerComponent implements OnInit, OnDestroy {
  @ViewChild("stakeInput") stakeInputRef!: ElementRef<HTMLInputElement>;
  @Output() setIndex = new EventEmitter<number>();
  @Input() context: string = "";
  placementService = inject(BetPlacementService);
  apiService = inject(ApiService);
  liveUpdateService = inject(LiveUpdateService);
  snackbar = inject(SnackbarService);
  popularEventsService = inject(PopularEventsService);
  coreService = inject(CoreService);
  loader = inject(LoaderService);
  exposureService = inject(ExposureService);
  router = inject(Router);
  stakeValues: string[] = defaultStakes;
  wlSlipSettings: any;
  params: any = null;
  form: FormGroup;
  liability = 0;
  potentialProfit = 0;
  msgOverlay!: Observable<boolean>;
  priceButtonFocus: boolean = false;
  $loading!: Observable<boolean>;
  exposureWithoutCurrentMarketExposure: number = 0;
  placementResponse = inject(PlacementOpsService);
  marketOps = inject(MarketOpsService);
  balanceService = inject(BalanceService);
  whitelabelConfig = inject(WhiteLabelConfigService);
  isBalanceInsufficient: boolean = false;
  shouldCancelUnmatchedBetsFirst: boolean = false;
  elRef = inject(ElementRef);
  readonly destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private readonly betService: BetService,
    protected readonly debuggingService: DebuggingService,
  ) {
    this.form = this.fb.group({
      price: ["", [Validators.required, Validators.min(1.01), Validators.max(1000), notNaNValidator()]],
      stake: ["", [Validators.required, notNaNValidator()]],
    });
  }

  get price() {
    return this.form.get("price");
  }

  get stake() {
    return this.form.get("stake");
  }

  get isSubmitDisabled() {
    return this.form.invalid || this.isBalanceInsufficient || this.shouldCancelUnmatchedBetsFirst || this.isPlacingInAnotherTab;
  }

  get isPlacingInAnotherTab() {
    return localStorage.getItem("placing_bet_flag") === "true";
  }

  get errorMessage() {
    if (this.shouldCancelUnmatchedBetsFirst) {
      return "Cancel unmatched bets first";
    }
    if (this.isBalanceInsufficient) {
      return "Insufficient balance";
    }
    return null;
  }

  get isStakeInvalid() {
    const stake = this.stake;
    return (stake?.hasError("min") || stake?.hasError("max") || stake?.hasError("notNumber")) && stake?.touched && stake?.dirty;
  }

  get isOddsInvalid() {
    const price = this.price;
    return (price?.errors?.["invalidOdd"] || price?.hasError("required") || price?.hasError("notNumber")) && price?.touched && price?.dirty;
  }

  get isAsianHandicap() {
    return this.params?.type?.includes("ASIAN_HANDICAP");
  }

  get userId() {
    return this.apiService.getUserToken()?.user?.ID;
  }

  get slipSettings() {
    return this.placementService.getSlipSettings()?.betSlipSettings;
  }

  @HostListener("document:keydown", ["$event"])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (!this.priceButtonFocus) return;

    if (event.key === "ArrowUp") {
      this.handlePriceIncrement();
      event.preventDefault();
    } else if (event.key === "ArrowDown") {
      this.handlePriceDecrement();
      event.preventDefault();
    }
  }

  ngOnInit() {
    this.preparePredictedBetsOnFormValueChange();
    this.betService.predictedBets$
      .pipe(combineLatestWith(this.exposureService.balanceData, this.betService.betsMarkets$, this.betService.nonPendingBets$))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ([predictedBets, balance, _, nonPendingBets]) => {
          if (
            Boolean(balance) &&
            Boolean(this.params) &&
            Boolean(this.params.type) &&
            Boolean(this.params.data) &&
            Boolean(predictedBets[this.params.data.topic ?? this.params.data.MARKET_ID]) &&
            Boolean(predictedBets[this.params.data.topic ?? this.params.data.MARKET_ID].stake) &&
            Boolean(predictedBets[this.params.data.topic ?? this.params.data.MARKET_ID].fOdds)
          ) {
            const validationResult = this.betService.evaluateExposureValidation(
              nonPendingBets.map((bet) => ({
                ...bet,
                betSize: bet.stakeRemaining === 0 ? (bet.stakeMatched ? bet.stakeMatched : bet.betSize) : bet.betSize,
                marketGroupId: this.betService.generateMarketGroupIdForCalculationBet(bet),
                dfOdds: bet.provider === MarketProvider.BF && bet.bettingType === "LINE" ? bet.averagePriceMatched : bet.dfOdds,
                rangeOdd: bet.averagePriceMatched ?? bet.dfOdds,
                sizeMatched: bet.sizeMatched,
              })),
              balance!,
              predictedBets[this.params.data.topic ?? this.params.data.MARKET_ID],
              "-1",
            );

            this.isBalanceInsufficient = Boolean(validationResult && "insufficientBalance" in validationResult && validationResult.insufficientBalance);
            this.shouldCancelUnmatchedBetsFirst = Boolean(
              validationResult && "cancelUnmatchedBetsFirst" in validationResult && validationResult.cancelUnmatchedBetsFirst,
            );
          } else {
            this.isBalanceInsufficient = false;
            this.shouldCancelUnmatchedBetsFirst = false;
          }
        },
      });

    this.exposureService.balanceData.pipe(takeUntil(this.destroy$)).subscribe(() => this.calculateNewExposure());
    this.$loading = combineLatest([
      this.loader.isLoading("bf_placebet"),
      this.loader.isLoading("current-user-order"),
      this.loader.isLoading("refresh_exposure"),
    ]).pipe(map(([placingBet, loadingBets, refreshingExposure]) => placingBet || loadingBets || refreshingExposure));

    this.getParamUpdates();

    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((values) => {
      const isSb = this.params?.data?.PROVIDER === "SB";
      const isLine = this.params?.type === "LINE";
      if (this.params && !isSb && !isLine) {
        this.params = {
          ...this?.params,
          data: {
            ...this.params?.data,
            F_ODDS: safeParseFloat(values.price),
          },
        };
      }
      const fOdds = this.params?.data?.F_ODDS;
      const _price = this.placementResponse.priceValue(values.price, fOdds);
      this.updateCalculations(_price, values.stake);

      if (this.params?.type && values?.price && values?.stake) {
        this.placementService.currentPlacement.next({
          ...this.params,
          data: {
            ...values,
            ...this.params?.data,
            PRICE: _price,
            price: values?.price,
          },
        });
      } else {
        this.placementService.currentPlacement.next(null);
      }
    });
    this.getSlipChange();

    this.msgOverlay = this.placementResponse.getMsgOverlay();
  }

  getSlipChange() {
    this.placementService.changeNotifier$.pipe(takeUntil(this.destroy$)).subscribe((type) => {
      if (type === "slipChange") {
        this.setStakeValues(this.params);
      }
    });
  }

  setStakeValues(params?: any) {
    const userSlipSettings = this.placementService.getSlipSettings();
    this.wlSlipSettings = this.placementService.getWLSlipSettings();
    this.stakeValues =
      userSlipSettings?.quickBetAmounts?.length > 0
        ? userSlipSettings?.quickBetAmounts
        : this.wlSlipSettings?.quickBetAmounts?.length > 0
          ? this.wlSlipSettings?.quickBetAmounts
          : defaultStakes;
    this.setStakeValidation(params);
  }

  setStakeValidation(params: any) {
    const sportId = params?.data?.SPORT_ID;
    const betConfig = this.whitelabelConfig?.betConfig;
    const minBet = betConfig?.[sportId]?.min_bet_size;
    const maxBet = betConfig?.[sportId]?.max_bet_size;

    const validators = [Validators.required, notNaNValidator()];
    if (minBet) validators.push(Validators.min(minBet));
    if (maxBet) validators.push(Validators.max(maxBet));

    this.stake?.setValidators(validators);
    this.stake?.updateValueAndValidity();
  }

  validOddValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = parseFloat(control.value);
      return validOdds.includes(value) ? null : { invalidOdd: true };
    };
  }

  preventNonNumeric(field: string, value: string): void {
    if (!value) return;

    const cleanedValue = String(value).replace(/[^0-9.]+/g, "");

    if (cleanedValue !== value) {
      this.form.patchValue({ [field]: safeParseFloat(cleanedValue) }, { emitEvent: false });
    }
  }

  onPriceInput(): void {
    // Trigger validation in real-time as user types odds
    this.form.updateValueAndValidity();
  }

  onPriceBlur(): void {
    const numericValue = parseFloat(this.form.value?.price);
    if (isNaN(numericValue) || this.params?.data?.PROVIDER === "SB" || this.isAsianHandicap) {
      return;
    }

    const closestValidValue = this.findClosest(numericValue, validOdds);
    if (closestValidValue !== numericValue) {
      this.form.patchValue({ price: closestValidValue }, { emitEvent: true });
    }
  }

  findClosest(num: number, arr: number[]): number {
    return arr.reduce((prev, curr) => (Math.abs(curr - num) < Math.abs(prev - num) ? curr : prev));
  }

  handlePriceIncrement() {
    const currentPrice = this.form.value?.price;
    const numericPrice = parseFloat(currentPrice);
    if (isNaN(numericPrice)) {
      return;
    }

    const nextHigher = validOdds.find((odds) => odds > numericPrice);
    if (nextHigher !== undefined) {
      this.form.patchValue({ price: nextHigher }, { emitEvent: true });
    }
  }

  handlePriceDecrement() {
    const currentPrice = this.form.value?.price;
    const numericPrice = parseFloat(currentPrice);
    if (isNaN(numericPrice)) {
      return;
    }

    const index = validOdds.findIndex((odds) => odds >= numericPrice);
    const nextLower = index > 0 ? validOdds[index - 1] : undefined;
    if (nextLower !== undefined) {
      this.form.patchValue({ price: nextLower }, { emitEvent: true });
    }
  }

  getParamUpdates() {
    this.placementService
      .getParams()
      .pipe(takeUntil(this.destroy$))
      .subscribe((params: { type: string; data: any }) => {
        const isOneClickBetting = this.slipSettings?.appearance === "oneClickBetting";

        if (params) {
          this.placementResponse.shouldClearParams = false;
          this.placementService.disablePlacement$.next(false);
          this.placementResponse.currentParams = params;
          setTimeout(() => {
            this.scrollToSection();
          }, 0);
        }

        if (params) {
          if (params?.type === "LINE" || params?.type?.includes("ASIAN_HANDICAP") || params?.data?.PROVIDER === "SB") {
            this.price?.clearValidators();
          } else {
            this.price?.addValidators([this.validOddValidator()]);
          }
          this.price?.updateValueAndValidity();
          if (!isOneClickBetting) this.resetStakeMark();
          this.setStakeValues(params);
        }

        this.params = params;

        const stake = isOneClickBetting ? safeParseFloat(this.slipSettings?.defaultStake) : "";
        this.form.patchValue({
          price: params?.data?.PRICE,
          stake,
        });
        if (params && stake) {
          this.markStake();
          this.updateCalculations(params?.data?.F_ODDS, stake);
          this.placementService.currentPlacement.next({
            ...params,
            data: {
              stake,
              ...params?.data,
              PRICE: params?.data?.F_ODDS,
              price: params?.data?.PRICE,
            },
          });
        }

        if (params?.data) {
          this.betService.betsMarkets$.next({
            ...this.betService.betsMarkets$.getValue(),
            [this.params.data.MARKET_ID]: {
              ...params.data,
              event: {
                name: params.data.eventName,
              },
              description: {
                bettingType: params.type,
              },
            },
          });

          this.placementResponse.setMsgOverlay(false);
          this.placementResponse.resetStatus();
        }
      });
  }

  handleSubmit() {
    if (this.form.valid) {
      // Check cross-tab protection first
      if (localStorage.getItem("placing_bet_flag") === "true") {
        return;
      }

      this.placementResponse.isPlacingBet = true;
      this.placementResponse.shouldClearParams = true;
      this.placementResponse.setMsgOverlay(true);
      const walletId = this.apiService.getUserWalletId();
      const { price, stake } = this.form.value;
      const { eventName, marketName, runnerName, runners, topic, minBet, maxBet, context, ...restParams } = this.params?.data;
      const data = {
        ...restParams,
        PRICE: +price,
        SIZE: +stake,
        USER_ID: this.userId,
        WALLET_ID: walletId,
        BETTING_TYPE: this.params.type,
      };

      // console.log(data);

      this.placementService
        .performPlacement(data)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            // console.log(response);
            if (response.status === "ok") {
              // console.log("API response after placement -> ", response);
              this.placementResponse.submittedParams = data;
              const betId = response?.result?.[0]?.ID;
              if (this.placementResponse.isPlacingBet) {
                this.placementResponse.setTransactionTimeout(betId);
              }
              this.popularEventsService.add({
                id: this.params?.data?.EVENT_ID,
                name: this.params?.data?.eventName,
              });
            } else {
              this.placementResponse.handleError(JSON.stringify(response?.result));
            }
          },
          error: (err) => {
            console.error(err);
            this.placementResponse.setMsgOverlay(false);
            this.placementResponse.isPlacingBet = false;
            localStorage.removeItem("placing_bet_flag");
          },
        });
    } else {
      console.warn("Form is not valid:", this.form.errors);
    }
  }

  updateCalculations(price: number, stake: number) {
    const { SIDE } = this.params?.data || {};

    if (!price || !stake) {
      this.liability = 0;
      this.potentialProfit = 0;
      return;
    }

    if (SIDE === "BACK") {
      this.potentialProfit = (price - 1) * stake;
      this.liability = stake;
    } else if (SIDE === "LAY") {
      this.liability = (price - 1) * stake;
      this.potentialProfit = this.liability;
    }
  }

  updateStake(value: string) {
    this.form.patchValue({
      stake: isNumeric(value) ? value : NumeralUtil.fromCompact(value?.toLowerCase()),
    });
    this.markStake();
  }

  onCancel() {
    // Clear the predicted bet for current market
    if (this.params?.data) {
      const marketGroupId = this.params.data.topic ?? this.params.data.MARKET_ID;
      const currentPredictedBets = this.betService.predictedBets$.getValue();
      delete currentPredictedBets[marketGroupId];
      this.betService.predictedBets$.next(currentPredictedBets);

      // Clear debugging data for this market
      const currentDebuggingData = this.debuggingService.exposureDebuggingObject.getValue();
      const marketPrefix = `${this.params.data.eventName} - ${this.params.data.marketName} -`.toUpperCase();

      // Remove all debugging entries for this market
      Object.keys(currentDebuggingData).forEach((key) => {
        if (key.startsWith(marketPrefix)) {
          delete currentDebuggingData[key];
        }
      });

      this.debuggingService.exposureDebuggingObject.next(currentDebuggingData);
    }

    this.placementService.setParams(null);
    this.placementService.currentPlacement.next(null);
  }

  markStake() {
    this.stake?.markAsTouched();
    this.stake?.markAsDirty();
  }

  resetStakeMark() {
    setTimeout(() => this.stakeInputRef?.nativeElement.focus(), 0);
    this.stake?.markAsUntouched();
    this.stake?.markAsPristine();
  }

  scrollToSection() {
    const targetElement = this.elRef.nativeElement.querySelector("#placementForm");
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.onCancel();
  }

  calculateNewExposure() {
    if (this.params && this.params.data?.MARKET_ID) {
      const leftOperand = Number(this.exposureService.balanceData.getValue()?.RISK_EXPOSURE) ?? 0;
      const rightOperand =
        this.exposureService.currentMarketExposures[this.params.data.MARKET_ID]?.length > 0
          ? Math.min(...this.exposureService.currentMarketExposures[this.params.data.MARKET_ID])
          : 0;

      this.exposureWithoutCurrentMarketExposure = leftOperand - (rightOperand < 0 ? rightOperand : 0);
    }

    const minPredictedMarketExposure =
      Object.values(this.exposureService.predictedMarketExposure).length >= 1 ? Math.min(...Object.values(this.exposureService.predictedMarketExposure)) : 0;
    return this.exposureWithoutCurrentMarketExposure + (minPredictedMarketExposure < 0 ? minPredictedMarketExposure : 0);
  }

  preparePredictedBetsOnFormValueChange() {
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe({
      next: (formValues) => {
        if (
          Boolean(this.params) &&
          Boolean(this.params.type) &&
          Boolean(this.params.data) &&
          Boolean(this.params.data.MARKET_ID) &&
          Boolean(this.params.data.SELECTION_ID) &&
          Boolean(this.params.data.SIDE) &&
          Boolean(this.params.data.PRICE) &&
          Boolean(formValues.price) &&
          Boolean(formValues.stake)
        ) {
          const predictedBet: PredictedBet = {
            marketId: this.params.data.MARKET_ID,
            marketName: this.params.data.marketName,
            selectionId: this.params.data.SELECTION_ID,
            side: this.params.data.SIDE,
            fOdds:
              this.params.data.marketName === "FANCY"
                ? (this.params.data.F_ODDS ??
                  this.betService.getFinalOdds(Number(formValues.price), this.params.data.PROVIDER ?? MarketProvider.BF, this.params.type === "LINE"))
                : this.betService.getFinalOdds(
                    Number(formValues.price),
                    this.params.data.PROVIDER && this.params.data.marketName === "0% Commission"
                      ? MarketProvider.BF
                      : (this.params.data.PROVIDER ?? MarketProvider.BF),
                    this.params.type === "LINE",
                  ),
            // fOdds: this.params.data.F_ODDS ??
            // this.betService.getFinalOdds(Number(formValues.price), this.params.data.PROVIDER ?? MarketProvider.BF, this.params.type === "LINE"),
            dfOdds: this.params.data.PRICE,
            stake: formValues.stake,
            bettingType: this.params.type,
            marketGroupId: this.params.data.topic ?? this.params.data.MARKET_ID,
            provider: this.params.data.PROVIDER,
            handicap: this.params.data.HANDICAP,
            handicapValues: this.params.data.runners.find((runner: any) => runner.selectionId === this.params.data.SELECTION_ID)?.handicapValues ?? [],
            averagePriceMatched: this.params.data.F_ODDS,
            runnerName: this.params.data.runnerName,
          };

          this.betService.predictedBets$.next({
            ...this.betService.predictedBets$.getValue(),
            [predictedBet.marketGroupId]: predictedBet,
          });
        }
      },
    });
  }

  showDebugDialog(event: Event) {
    event.stopPropagation();

    const message = JSON.stringify(this.debuggingService.exposureDebuggingObject.getValue());
    this.debuggingService.showDebuggingAlert(message);
  }

  // getTotalExposureDebugging(debuggingObjects: Record<string, ExposureDebuggingObject>) {
  //   if (this.params && this.params.data && this.params.data.eventName && this.params.data.marketName) {
  //     return debuggingObjects[`${this.params.data.eventName} - ${this.params.data.marketName} - -1`.toUpperCase()]?.newTotalExposure;
  //   }
  //
  //   return 0;
  // }

  getTotalExposureDebugging(debuggingObjects: Record<string, ExposureDebuggingObject>) {
    if (this.params && this.params.data && this.params.data.eventName && this.params.data.marketName) {
      // console.log("debuggingObjects", debuggingObjects);
      const expectedKey = `${this.params.data.eventName} - ${this.params.data.marketName} - -1`.toUpperCase();

      // If the expected key exists, use it
      if (debuggingObjects[expectedKey]) {
        return debuggingObjects[expectedKey].newTotalExposure ?? 0;
      }

      // Otherwise, use the first available key for this market
      const availableKeys = Object.keys(debuggingObjects);
      const marketPrefix = `${this.params.data.eventName} - ${this.params.data.marketName} -`.toUpperCase();
      const matchingKey = availableKeys.find((key) => key.startsWith(marketPrefix));

      if (matchingKey) {
        return debuggingObjects[matchingKey].newTotalExposure ?? 0;
      }
    }

    return 0;
  }

  getExposureWhenUnmatched(debuggingObjects: Record<string, ExposureDebuggingObject>) {
    return debuggingObjects[`${this.params.data.eventName} - ${this.params.data.marketName} - -1`.toUpperCase()]?.exposureWhenUnmatched;
  }
}
